// calculators/_shared/multiSeriesChart.ts
// Shared multi-series SVG balance-over-time line chart.
// No deps. Inlined per-bundle by esbuild (this dir has no src/index.ts, so
// build.config.js does NOT treat it as a standalone entry point).
//
// Consumers pass one or more series of {month, balance} points plus a label
// and color. The renderer auto-scales both axes across all series, downsamples
// each series to <= 60 points, draws a legend, and can annotate a "still owing"
// series whose balance never reaches zero within the window.

const SVG_NS = "http://www.w3.org/2000/svg";

const VIEW_W = 600;
const VIEW_H = 300;
const PAD_LEFT = 56;
const PAD_RIGHT = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 56; // extra room for legend row

export interface SeriesPoint {
  month: number;
  balance: number;
}

export interface ChartSeries {
  label: string;
  color: string;
  points: SeriesPoint[];
  /** When true, draw a small "still owing" annotation at the series' last point. */
  stillOwing?: boolean;
}

function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {}
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) {
    node.setAttribute(k, String(v));
  }
  return node;
}

function niceCeil(n: number): number {
  if (n <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(n)));
  const norm = n / mag;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * mag;
}

/**
 * Downsample a series to at most `maxPoints` points by even index stride.
 * Always keeps the first and last point so the trajectory endpoints are exact.
 */
export function downsample(points: SeriesPoint[], maxPoints = 60): SeriesPoint[] {
  if (points.length <= maxPoints) return points;
  const stride = Math.ceil(points.length / maxPoints);
  const out: SeriesPoint[] = [];
  for (let i = 0; i < points.length; i += stride) out.push(points[i]);
  const last = points[points.length - 1];
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

/**
 * Build a {month, balance} series from a SimulationResult-style months array.
 * Prepends a month-0 point using the first month's opening balance.
 */
export function seriesFromMonths(
  months: { month: number; openingBalance: number; closingBalance: number }[]
): SeriesPoint[] {
  if (months.length === 0) return [{ month: 0, balance: 0 }];
  const pts: SeriesPoint[] = [{ month: 0, balance: months[0].openingBalance }];
  for (const m of months) pts.push({ month: m.month, balance: m.closingBalance });
  return pts;
}

export function clearChart(svgId: string): void {
  const svg = document.getElementById(svgId) as unknown as SVGSVGElement | null;
  if (!svg) return;
  while (svg.firstChild) svg.removeChild(svg.firstChild);
}

export function renderMultiSeriesChart(
  svg: SVGSVGElement,
  seriesList: ChartSeries[],
  stillOwingLabel = "still owing"
): void {
  while (svg.firstChild) svg.removeChild(svg.firstChild);

  svg.setAttribute("viewBox", `0 0 ${VIEW_W} ${VIEW_H}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  svg.setAttribute("role", "img");

  // Downsample each series first.
  const series = seriesList
    .filter((s) => s.points.length > 0)
    .map((s) => ({ ...s, points: downsample(s.points) }));

  if (series.length === 0) return;

  const allPoints = series.flatMap((s) => s.points);
  const maxMonth = Math.max(...allPoints.map((p) => p.month), 1);
  const maxBalance = niceCeil(Math.max(...allPoints.map((p) => p.balance), 1));

  const plotW = VIEW_W - PAD_LEFT - PAD_RIGHT;
  const plotH = VIEW_H - PAD_TOP - PAD_BOTTOM;

  const xFor = (m: number): number => PAD_LEFT + (m / maxMonth) * plotW;
  const yFor = (b: number): number => PAD_TOP + (1 - b / maxBalance) * plotH;

  // ── Grid + Y labels ──────────────────────────────────────────────────────
  const gridGroup = el("g", { stroke: "#E2E8F0", "stroke-width": 1 });
  const labelGroup = el("g", {
    "font-family": "Inter, system-ui, sans-serif",
    "font-size": 11,
    fill: "#64748B",
  });

  const Y_TICKS = 4;
  for (let i = 0; i <= Y_TICKS; i++) {
    const v = (maxBalance * i) / Y_TICKS;
    const y = yFor(v);
    gridGroup.appendChild(
      el("line", { x1: PAD_LEFT, x2: VIEW_W - PAD_RIGHT, y1: y, y2: y })
    );
    const lbl = el("text", { x: PAD_LEFT - 8, y: y + 4, "text-anchor": "end" });
    lbl.textContent = `$${Math.round(v).toLocaleString()}`;
    labelGroup.appendChild(lbl);
  }

  // X-axis month labels — ~5 evenly spaced.
  const X_TICKS = Math.min(5, maxMonth);
  const baselineY = yFor(0);
  for (let i = 0; i <= X_TICKS; i++) {
    const m = Math.round((maxMonth * i) / X_TICKS);
    const lbl = el("text", {
      x: xFor(m),
      y: baselineY + 18,
      "text-anchor": "middle",
    });
    lbl.textContent = `M${m}`;
    labelGroup.appendChild(lbl);
  }

  svg.appendChild(gridGroup);
  svg.appendChild(labelGroup);

  // X axis line.
  svg.appendChild(
    el("line", {
      x1: PAD_LEFT,
      y1: baselineY,
      x2: VIEW_W - PAD_RIGHT,
      y2: baselineY,
      stroke: "#94A3B8",
      "stroke-width": 1,
    })
  );

  // ── Series lines ─────────────────────────────────────────────────────────
  series.forEach((s) => {
    const linePoints = s.points.map((p) => `${xFor(p.month)},${yFor(p.balance)}`);
    const linePath = `M ${linePoints.join(" L ")}`;
    const line = el("path", {
      d: linePath,
      fill: "none",
      stroke: s.color,
      "stroke-width": 2.5,
      "stroke-linejoin": "round",
      "stroke-linecap": "round",
    });
    svg.appendChild(line);

    const lineNode = line as SVGPathElement;
    const measured =
      typeof lineNode.getTotalLength === "function" ? lineNode.getTotalLength() : 0;
    const totalLength = measured > 0 ? measured : 1000;
    lineNode.style.strokeDasharray = String(totalLength);
    lineNode.style.strokeDashoffset = String(totalLength);
    requestAnimationFrame(() => {
      lineNode.style.transition = "stroke-dashoffset 320ms ease-out";
      lineNode.style.strokeDashoffset = "0";
    });

    // "Still owing" annotation at the last point of the series.
    if (s.stillOwing && s.points.length > 0) {
      const last = s.points[s.points.length - 1];
      const cx = xFor(last.month);
      const cy = yFor(last.balance);
      svg.appendChild(
        el("circle", { cx, cy, r: 3.5, fill: s.color })
      );
      const ann = el("text", {
        x: Math.min(cx - 4, VIEW_W - PAD_RIGHT - 4),
        y: Math.max(cy - 8, PAD_TOP + 10),
        "text-anchor": "end",
        "font-family": "Inter, system-ui, sans-serif",
        "font-size": 11,
        "font-weight": 700,
        fill: s.color,
      });
      ann.textContent = stillOwingLabel;
      svg.appendChild(ann);
    }
  });

  // ── Legend ───────────────────────────────────────────────────────────────
  const legend = el("g", {
    "font-family": "Inter, system-ui, sans-serif",
    "font-size": 12,
    fill: "#334155",
  });
  let lx = PAD_LEFT;
  const ly = VIEW_H - 14;
  series.forEach((s) => {
    legend.appendChild(
      el("rect", { x: lx, y: ly - 9, width: 14, height: 4, rx: 2, fill: s.color })
    );
    const text = el("text", { x: lx + 20, y: ly });
    text.textContent = s.label;
    legend.appendChild(text);
    // Advance by an estimate: label width ~7px/char + swatch + gap.
    lx += 20 + s.label.length * 7 + 24;
  });
  svg.appendChild(legend);

  const title = el("title");
  title.textContent = "Balance over time";
  svg.appendChild(title);
}
